import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface CommonFabricPropResult<T> {
  value: T | undefined;
  isMixed: boolean;
  setValue: (next: T) => void;
}

interface Aggregate<T> {
  value: T | undefined;
  isMixed: boolean;
}

function aggregate<T>(objects: fabric.Object[], key: string): Aggregate<T> {
  if (objects.length === 0) return { value: undefined, isMixed: false };
  const first = (objects[0] as unknown as Record<string, T>)[key];
  for (let i = 1; i < objects.length; i++) {
    const v = (objects[i] as unknown as Record<string, T>)[key];
    if (v !== first) return { value: undefined, isMixed: true };
  }
  return { value: first, isMixed: false };
}

/**
 * Sibling of useCommonFabricData for standard Fabric props (left, fontSize,
 * fill, angle, …). Aggregates the prop across every selected object — equal
 * across all → value, otherwise isMixed — and fans setValue out to all via
 * editor.objects.setProp. For a single-object array this degrades to the
 * plain single-object read/write (isMixed always false).
 */
export function useCommonFabricProp<T>(
  objects: fabric.Object[],
  key: string,
  editor: FabricEditor,
): CommonFabricPropResult<T> {
  const objectSet = useMemo(() => new Set(objects), [objects]);
  const read = useCallback(() => aggregate<T>(objects, key), [objects, key]);
  const [state, setState] = useState<Aggregate<T>>(read);

  useEffect(() => {
    setState(read());
    const off = editor.on('object:modified', (e) => {
      if (objectSet.has(e.target as fabric.Object)) setState(read());
    });
    return () => off();
  }, [editor, objectSet, read]);

  const setValue = useCallback(
    (next: T) => {
      for (const obj of objects) {
        editor.objects.setProp(obj, key as keyof fabric.Object, next as never);
      }
      setState({ value: next, isMixed: false });
    },
    [editor, objects, key],
  );

  return { value: state.value, isMixed: state.isMixed, setValue };
}
